import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ChevronDownIcon, ChevronUpIcon, CheckIcon, AlertCircleIcon } from 'lucide-react';
export function Pricing() {
  const [expandedAddOn, setExpandedAddOn] = useState<string | null>(null);
  const toggleAddOn = (section: string) => {
    setExpandedAddOn(expandedAddOn === section ? null : section);
  };
  const scrollToConsultation = () => {
    window.location.href = 'mailto:info@bright-pixxel.com?subject=New%20Enquiry';
  };
  const webDesignFeatures = ['Up to 5 pages', 'Responsive mobile design', 'Enquiry form', 'Basic SEO setup', 'Blog setup option', '1 business email address', 'Fast loading speeds'];
  const ecommerceFeatures = ['Unlimited products', 'PayPal/Stripe integration', 'Basic product search', 'Responsive shop layout', 'Blog (optional)', 'Basic SEO', '3-months free hosting', '24/7 ticket support (90 days)'];
  const maintenanceFeatures = ['Hosting & domain management', 'SSL renewal', 'Security monitoring', 'Monthly backups', 'Plugin/theme updates', 'Basic email support'];
  const consultationPoints = ['Review your website goals', 'Check if you need booking systems, payment links, or forms.', 'Discuss whether you need content writing', 'Review design inspiration websites', 'Identify SEO needs', 'Help you choose colours and branding', 'Confirm your pages and features', 'Provide a tailored quote based on your exact requirements'];
  const contentWritingAddOns = ['Per page (up to 400 words)', 'Product descriptions', 'About/Story writing', 'SEO keyword research'];
  const brandingAddOns = ['Logo creation', 'Brand colour palette + typography selection', 'Favicon design'];
  const pagesAddOns = ['Extra web pages (beyond the included amount)'];
  const featuresAddOns = ['Additional forms', 'Payment links/buttons', 'Booking system integration', 'Social media integration', 'Photo editing'];
  const faqs = [{
    question: 'How does the free consultation work?',
    answer: "Book a 30-minute call where we discuss your needs, goals, and vision. We'll provide a personalised quote based on your specific requirements."
  }, {
    question: 'Are there any hidden fees?',
    answer: "No. All costs are transparent and agreed upon during your consultation. You'll receive a detailed quote before any work begins."
  }, {
    question: 'Can I upgrade my package later?',
    answer: "Yes, you can add features or pages at any time. We'll provide pricing for any additions you need."
  }, {
    question: 'Do you offer payment plans?',
    answer: 'We require 50% payment upfront before starting your project, and the remaining 50% upon completion. This ensures commitment from both sides and allows us to deliver your website without delays.'
  }, {
    question: 'What if I need something not listed?',
    answer: "We create custom solutions for unique requirements. Discuss your needs during the free consultation and we'll provide a tailored quote."
  }];
  return <>
      <Helmet data-id="element-1689">
        <title data-id="element-1690">
          Pricing | Bright Pixxel Studios – Transparent Web Design Costs
        </title>
        <meta name="description" content="Clear, honest pricing for web design, e-commerce, and maintenance. Packages from £399. Book a free consultation to get your personalised quote." data-id="element-1691" />
        <link rel="canonical" href="https://bright-pixxel.com/pricing" data-id="element-1692" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-white" data-id="element-1693">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1694">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1695">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="text-4xl md:text-5xl font-bold mb-6 text-[#0C1B26]" data-id="element-1696">
              Simple, Transparent Pricing
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-1697">
              Every website is built specifically around your business needs. A
              tailored package is created after your free consultation.
            </motion.p>
            <motion.button initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} onClick={scrollToConsultation} className="bg-[#0DC3DC] hover:bg-[#26D0E8] text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl" data-id="element-1698">
              Book Your Free Consultation
            </motion.button>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 bg-gray-50" data-id="element-1699">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1700">
          <div className="max-w-4xl mx-auto" data-id="element-1701">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center" data-id="element-1702">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="flex flex-col items-center" data-id="element-1703">
                <CheckIcon size={32} className="text-[#0DC3DC] mb-2" data-id="element-1704" />
                <p className="font-bold text-gray-900" data-id="element-1705">Transparent pricing</p>
                <p className="text-sm text-gray-600" data-id="element-1706">No hidden fees</p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="flex flex-col items-center" data-id="element-1707">
                <CheckIcon size={32} className="text-[#0DC3DC] mb-2" data-id="element-1708" />
                <p className="font-bold text-gray-900" data-id="element-1709">Fast turnaround</p>
                <p className="text-sm text-gray-600" data-id="element-1710">7–14 days</p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="flex flex-col items-center" data-id="element-1711">
                <CheckIcon size={32} className="text-[#0DC3DC] mb-2" data-id="element-1712" />
                <p className="font-bold text-gray-900" data-id="element-1713">Flexible packages</p>
                <p className="text-sm text-gray-600" data-id="element-1714">Built for your needs</p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards - Side by Side */}
      <section className="py-16 bg-white" data-id="element-1715">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1716">
          <div className="max-w-6xl mx-auto" data-id="element-1717">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-center mb-12" data-id="element-1718">
              <div className="inline-flex items-center gap-2 bg-[#0DC3DC]/10 px-4 py-2 rounded-full mb-4" data-id="element-1719">
                <AlertCircleIcon size={20} className="text-[#0DC3DC]" data-id="element-1720" />
                <span className="text-sm font-medium text-[#0DC3DC]" data-id="element-1721">
                  Starting prices shown below
                </span>
              </div>
              <h2 className="text-3xl font-bold text-[#0C1B26] mb-3" data-id="element-1722">
                Choose Your Starting Package
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1723">
                These are minimum starting prices. Your final quote will be
                tailored to your exact requirements during the free
                consultation.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-1724">
              {/* Web Design */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="bg-[#212C34] rounded-lg p-6 border border-[#D4AF37] shadow-lg flex flex-col" data-id="element-1725">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1726">
                  Web Design
                </h3>
                <div className="mb-4" data-id="element-1727">
                  <span className="text-sm text-white/70" data-id="element-1728">Starting from</span>
                  <p className="text-4xl font-bold text-[#0DC3DC]" data-id="element-1729">£399</p>
                </div>

                <ul className="space-y-2 mb-6 flex-grow" data-id="element-1730">
                  {webDesignFeatures.map((feature, index) => <li key={index} className="flex items-start gap-2 text-white text-sm" data-id="element-1731">
                      <CheckIcon size={16} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-1732" />
                      <span data-id="element-1733">{feature}</span>
                    </li>)}
                </ul>

                <button onClick={scrollToConsultation} className="w-full bg-[#0DC3DC] hover:bg-[#26D0E8] text-white py-3 rounded-lg font-bold transition-all duration-300" data-id="element-1734">
                  Get Your Quote
                </button>
              </motion.div>

              {/* E-Commerce */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="bg-[#212C34] rounded-lg p-6 border-2 border-[#0DC3DC] shadow-xl flex flex-col relative" data-id="element-1735">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2" data-id="element-1736">
                  <span className="bg-[#0DC3DC] text-white px-4 py-1 rounded-full text-xs font-bold" data-id="element-1737">
                    POPULAR
                  </span>
                </div>

                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1738">
                  E-Commerce
                </h3>
                <div className="mb-4" data-id="element-1739">
                  <span className="text-sm text-white/70" data-id="element-1740">Starting from</span>
                  <p className="text-4xl font-bold text-[#0DC3DC]" data-id="element-1741">£899</p>
                </div>

                <ul className="space-y-2 mb-6 flex-grow" data-id="element-1742">
                  {ecommerceFeatures.map((feature, index) => <li key={index} className="flex items-start gap-2 text-white text-sm" data-id="element-1743">
                      <CheckIcon size={16} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-1744" />
                      <span data-id="element-1745">{feature}</span>
                    </li>)}
                </ul>

                <button onClick={scrollToConsultation} className="w-full bg-[#0DC3DC] hover:bg-[#26D0E8] text-white py-3 rounded-lg font-bold transition-all duration-300" data-id="element-1746">
                  Start Your Store
                </button>
              </motion.div>

              {/* Maintenance */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="bg-[#212C34] rounded-lg p-6 border border-[#D4AF37] shadow-lg flex flex-col" data-id="element-1747">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1748">
                  Maintenance
                </h3>
                <div className="mb-4" data-id="element-1749">
                  <span className="text-sm text-white/70" data-id="element-1750">Starting from</span>
                  <p className="text-4xl font-bold text-[#0DC3DC]" data-id="element-1751">
                    £79<span className="text-lg" data-id="element-1752">/mo</span>
                  </p>
                </div>

                <ul className="space-y-2 mb-6 flex-grow" data-id="element-1753">
                  {maintenanceFeatures.map((feature, index) => <li key={index} className="flex items-start gap-2 text-white text-sm" data-id="element-1754">
                      <CheckIcon size={16} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-1755" />
                      <span data-id="element-1756">{feature}</span>
                    </li>)}
                </ul>

                <a href="/website-care" className="block w-full bg-[#0DC3DC] hover:bg-[#26D0E8] text-white py-3 rounded-lg font-bold transition-all duration-300 text-center" data-id="element-1757">
                  View Plans
                </a>
              </motion.div>
            </div>

            {/* Highlight Box */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-[#0DC3DC]/10 border-l-4 border-[#0DC3DC] p-6 rounded-lg" data-id="element-1758">
              <p className="text-gray-900 font-medium text-center" data-id="element-1759">
                <strong data-id="element-1760">
                  Every website is built specifically around your business
                  needs.
                </strong>{' '}
                A tailored package is created after your free consultation.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Free Consultation Section */}
      <section id="consultation-section" className="py-16 bg-[#212C34]" data-id="element-1761">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1762">
          <div className="max-w-3xl mx-auto" data-id="element-1763">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-1764">
              <h2 className="text-3xl font-bold mb-3 text-white text-center" data-id="element-1765">
                What We'll Cover in Your Free 30-Minute Consultation
              </h2>
              <p className="text-white/80 text-center mb-8" data-id="element-1766">
                This consultation increases trust and ensures you get exactly
                what you need.
              </p>

              <div className="bg-[#212C34] rounded-lg p-8 border border-[#D4AF37] mb-8" data-id="element-1767">
                <p className="text-white font-medium mb-4" data-id="element-1768">
                  During your consultation we will:
                </p>
                <ul className="space-y-3" data-id="element-1769">
                  {consultationPoints.map((point, index) => <li key={index} className="flex items-start gap-3 text-white" data-id="element-1770">
                      <CheckIcon size={20} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-1771" />
                      <span data-id="element-1772">{point}</span>
                    </li>)}
                </ul>
              </div>

              <div className="text-center" data-id="element-1773">
                <a href="mailto:info@bright-pixxel.com?subject=Free%20Consultation%20Request" className="inline-block bg-[#0DC3DC] hover:bg-[#26D0E8] text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl" data-id="element-1774">
                  Book Your Free Consultation
                </a>
                <p className="text-white/70 text-sm mt-4" data-id="element-1775">
                  This shows professionalism and reduces pricing arguments
                  later.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Optional Add-Ons */}
      <section className="py-16 bg-white" data-id="element-1776">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1777">
          <div className="max-w-3xl mx-auto" data-id="element-1778">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-center mb-8" data-id="element-1779">
              <h2 className="text-3xl font-bold mb-3 text-[#0C1B26]" data-id="element-1780">
                Optional Add-Ons
              </h2>
              <p className="text-gray-600" data-id="element-1781">
                Shown for transparency, not complexity. Add what you need.
              </p>
            </motion.div>

            <div className="space-y-4" data-id="element-1782">
              {/* Content Writing */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1783">
                <button onClick={() => toggleAddOn('content')} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-1784">
                  <span className="font-bold text-gray-900 text-lg" data-id="element-1785">
                    Content Writing
                  </span>
                  {expandedAddOn === 'content' ? <ChevronUpIcon size={24} className="text-[#0DC3DC]" data-id="element-1786" /> : <ChevronDownIcon size={24} className="text-[#0DC3DC]" data-id="element-1787" />}
                </button>
                <AnimatePresence data-id="element-1788">
                  {expandedAddOn === 'content' && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.3
                }} className="overflow-hidden" data-id="element-1789">
                      <div className="p-6 bg-white" data-id="element-1790">
                        <ul className="space-y-2" data-id="element-1791">
                          {contentWritingAddOns.map((addon, index) => <li key={index} className="flex items-start gap-2 text-gray-700" data-id="element-1792">
                              <CheckIcon size={16} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-1793" />
                              <span data-id="element-1794">{addon}</span>
                            </li>)}
                        </ul>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>

              {/* Branding */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1795">
                <button onClick={() => toggleAddOn('branding')} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-1796">
                  <span className="font-bold text-gray-900 text-lg" data-id="element-1797">
                    Branding
                  </span>
                  {expandedAddOn === 'branding' ? <ChevronUpIcon size={24} className="text-[#0DC3DC]" data-id="element-1798" /> : <ChevronDownIcon size={24} className="text-[#0DC3DC]" data-id="element-1799" />}
                </button>
                <AnimatePresence data-id="element-1800">
                  {expandedAddOn === 'branding' && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.3
                }} className="overflow-hidden" data-id="element-1801">
                      <div className="p-6 bg-white" data-id="element-1802">
                        <ul className="space-y-2" data-id="element-1803">
                          {brandingAddOns.map((addon, index) => <li key={index} className="flex items-start gap-2 text-gray-700" data-id="element-1804">
                              <CheckIcon size={16} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-1805" />
                              <span data-id="element-1806">{addon}</span>
                            </li>)}
                        </ul>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>

              {/* Pages */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1807">
                <button onClick={() => toggleAddOn('pages')} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-1808">
                  <span className="font-bold text-gray-900 text-lg" data-id="element-1809">Pages</span>
                  {expandedAddOn === 'pages' ? <ChevronUpIcon size={24} className="text-[#0DC3DC]" data-id="element-1810" /> : <ChevronDownIcon size={24} className="text-[#0DC3DC]" data-id="element-1811" />}
                </button>
                <AnimatePresence data-id="element-1812">
                  {expandedAddOn === 'pages' && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.3
                }} className="overflow-hidden" data-id="element-1813">
                      <div className="p-6 bg-white" data-id="element-1814">
                        <ul className="space-y-2" data-id="element-1815">
                          {pagesAddOns.map((addon, index) => <li key={index} className="flex items-start gap-2 text-gray-700" data-id="element-1816">
                              <CheckIcon size={16} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-1817" />
                              <span data-id="element-1818">{addon}</span>
                            </li>)}
                        </ul>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>

              {/* Features */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.4
            }} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1819">
                <button onClick={() => toggleAddOn('features')} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-1820">
                  <span className="font-bold text-gray-900 text-lg" data-id="element-1821">
                    Features
                  </span>
                  {expandedAddOn === 'features' ? <ChevronUpIcon size={24} className="text-[#0DC3DC]" data-id="element-1822" /> : <ChevronDownIcon size={24} className="text-[#0DC3DC]" data-id="element-1823" />}
                </button>
                <AnimatePresence data-id="element-1824">
                  {expandedAddOn === 'features' && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.3
                }} className="overflow-hidden" data-id="element-1825">
                      <div className="p-6 bg-white" data-id="element-1826">
                        <ul className="space-y-2" data-id="element-1827">
                          {featuresAddOns.map((addon, index) => <li key={index} className="flex items-start gap-2 text-gray-700" data-id="element-1828">
                              <CheckIcon size={16} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-1829" />
                              <span data-id="element-1830">{addon}</span>
                            </li>)}
                        </ul>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="mt-8 text-center" data-id="element-1831">
              <p className="text-gray-600 italic mb-6" data-id="element-1832">
                These feel small, predictable, and fair.
              </p>
              <button onClick={scrollToConsultation} className="bg-[#0DC3DC] hover:bg-[#26D0E8] text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl" data-id="element-1833">
                Get Your Free Quote
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50" data-id="element-1834">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1835">
          <div className="max-w-3xl mx-auto" data-id="element-1836">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl font-bold mb-8 text-[#0C1B26] text-center" data-id="element-1837">
              Frequently Asked Questions
            </motion.h2>

            <div className="space-y-4" data-id="element-1838">
              {faqs.map((faq, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-white rounded-lg p-6 shadow-sm" data-id="element-1839">
                  <h3 className="text-lg font-bold mb-3 text-gray-900" data-id="element-1840">
                    {faq.question}
                  </h3>
                  <p className="text-gray-700 leading-relaxed" data-id="element-1841">{faq.answer}</p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#212C34]" data-id="element-1842">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1843">
          <div className="max-w-2xl mx-auto text-center" data-id="element-1844">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-1845">
              <h2 className="text-3xl font-bold mb-4 text-white" data-id="element-1846">
                Ready to Start Your Project?
              </h2>
              <p className="text-white/80 mb-8 text-lg" data-id="element-1847">
                Book your free consultation today and get a personalised quote
                tailored to your exact needs.
              </p>
              <a href="mailto:info@bright-pixxel.com?subject=Free%20Consultation%20Request" className="inline-block bg-[#0DC3DC] hover:bg-[#26D0E8] text-white px-10 py-5 rounded-lg font-bold text-xl transition-all duration-300 shadow-lg hover:shadow-xl" data-id="element-1848">
                Start Your Project Today
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}